import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { CardMovimentoConciliado } from "../conciliacao/CardMovimentoConciliado";
import { ModalDesfazarConciliacao } from "../conciliacao/ModalDesfazarConciliacao";
import { SeletorEmpresaConciliacao } from "../conciliacao/SeletorEmpresaConciliacao";
import { useDesfazer, useMovimentos } from "../../lib/conciliacao/hooks";
import type { MovimentoConciliado } from "../../lib/conciliacao/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Pagina "Desfazer conciliacao" (FE-4 — Tela 4 §10.5).
 *
 * LEITURA dos movimentos JA conciliados (status "ativa") + a acao de DESFAZER.
 * Espelha a estrutura do `Conciliacao.tsx` (seletor de empresa + conteudo
 * condicional), mas lista MOVIMENTOS (nao a fila) e a escrita REVERTE.
 *
 * PAGINACAO POR OFFSET (a resposta e array PURO, sem total — gapB do backend):
 *  - `offset = page * LIMITE`; "Anterior" desabilitada com `page === 0`;
 *  - "Proxima" desabilitada quando a pagina veio incompleta (`length < LIMITE`);
 *  - "Mostrando X-Y" com X = offset+1 e Y = offset+length (SEM total).
 * Trocar de empresa RESETA `page=0` e FECHA o modal aberto.
 *
 * DESFAZER e gated por `desfazer_conciliacao` (botao oculto sem a permissao,
 * resolvido AQUI e passado como prop — nunca um hook dentro de `.map`). A escrita
 * NUNCA dispara por clique unico: clicar "Desfazer" ABRE o passo
 * `ModalDesfazarConciliacao` com a justificativa; so o Confirmar daquele passo
 * dispara a mutacao. No sucesso, o modal fecha e a justificativa zera (a lista
 * revalida pelo hook).
 *
 * Erro de NEGOCIO do desfazer (desfazer reverte DINHEIRO — falha NUNCA pode ser
 * silenciosa). Espelha o `tratarErroNegocio` do `FilaConciliacao` (via
 * `erro.status`, pois a mensagem generica do `ErroHttp` nao distingue 409 de 422):
 *  - 409 → "ja desfeita" + invalida `["conciliacao","movimentos",empresaId]`
 *    (outro operador desfez antes) E FECHA o modal (segue o FilaConciliacao, que
 *    no 409 fecha o passo e revalida); a mensagem aparece na PAGINA;
 *  - 422 → "Justificativa invalida." (defesa — o FE gateia >=10, mas o backend e
 *    o gate real); modal PERMANECE aberto p/ o operador corrigir e repetir;
 *  - 503 → "Modulo de conciliacao indisponivel no momento."; modal aberto;
 *  - outro `ErroHttp` → `erro.message`; modal aberto;
 *  - nao-`ErroHttp` → fallback generico; modal aberto.
 * NUNCA ecoa o corpo do servidor — so textos fixos / `erro.message` generico.
 */

const LIMITE = 10;

export function TelaDesfazarConciliacao(): JSX.Element {
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selecionado, setSelecionado] = useState<MovimentoConciliado | null>(
    null,
  );
  const [justificativa, setJustificativa] = useState("");
  // Mensagem de erro de negocio do desfazer (409/422/503/demais) ao operador.
  const [erroDesfazer, setErroDesfazer] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const podeDesfazer = useTemPermissao("desfazer_conciliacao");
  const offset = page * LIMITE;
  const consulta = useMovimentos(empresaId, {
    status: "ativa",
    limit: LIMITE,
    offset,
  });
  const mutacao = useDesfazer(empresaId ?? "");
  const desfazendo = mutacao.isPending;

  function aoSelecionarEmpresa(novaEmpresa: string): void {
    setEmpresaId(novaEmpresa);
    setPage(0);
    setSelecionado(null);
    setJustificativa("");
    setErroDesfazer(null);
  }

  function abrirDesfazer(movimento: MovimentoConciliado): void {
    setJustificativa("");
    setErroDesfazer(null);
    setSelecionado(movimento);
  }

  function fecharModal(): void {
    setSelecionado(null);
    setJustificativa("");
  }

  function confirmarDesfazer(): void {
    if (selecionado === null) return;
    mutacao.mutate(
      { movimentoId: selecionado.id, justificativa },
      {
        onSuccess: () => {
          // O hook ja invalida os movimentos da empresa (refetch). Aqui so
          // fechamos o passo, limpamos a justificativa e o erro de negocio.
          setErroDesfazer(null);
          fecharModal();
        },
        onError: (erro) => {
          tratarErroDesfazer(erro);
        },
      },
    );
  }

  /**
   * Mapeia o erro do desfazer numa mensagem estavel (espelha o `tratarErroNegocio`
   * do `FilaConciliacao`). O 409 FECHA o modal e revalida (outro operador desfez
   * antes); os demais MANTEM o modal aberto p/ o operador ver e ajustar/repetir.
   */
  function tratarErroDesfazer(erro: unknown): void {
    if (erro instanceof ErroHttp && erro.status === 409) {
      // Outro operador desfez antes — fecha o passo, revalida a lista e avisa.
      setErroDesfazer("Esta conciliação já foi desfeita — atualizando a lista.");
      fecharModal();
      void queryClient.invalidateQueries({
        queryKey: ["conciliacao", "movimentos", empresaId],
      });
      return;
    }
    if (erro instanceof ErroHttp && erro.status === 422) {
      setErroDesfazer("Justificativa inválida.");
      return;
    }
    if (erro instanceof ErroHttp && erro.moduloIndisponivel) {
      setErroDesfazer("Módulo de conciliação indisponível no momento.");
      return;
    }
    if (erro instanceof ErroHttp) {
      setErroDesfazer(erro.message);
      return;
    }
    setErroDesfazer("Não foi possível desfazer a conciliação.");
  }

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">
        Desfazer conciliação
      </h1>

      <SeletorEmpresaConciliacao
        selecionada={empresaId}
        aoSelecionar={aoSelecionarEmpresa}
      />

      {erroDesfazer !== null && selecionado === null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroDesfazer}
        </p>
      )}

      {empresaId === null ? (
        <p className="font-corpo text-base text-off-black">
          Selecione uma empresa para ver as conciliações.
        </p>
      ) : (
        <CorpoMovimentos
          consulta={consulta}
          page={page}
          offset={offset}
          podeDesfazer={podeDesfazer}
          aoDesfazer={abrirDesfazer}
          paginaAnterior={() => setPage((p) => Math.max(0, p - 1))}
          proximaPagina={() => setPage((p) => p + 1)}
        />
      )}

      {selecionado !== null && (
        <ModalDesfazarConciliacao
          movimento={selecionado}
          justificativa={justificativa}
          aoMudarJustificativa={setJustificativa}
          aoConfirmar={confirmarDesfazer}
          aoCancelar={fecharModal}
          desfazendo={desfazendo}
          erro={erroDesfazer}
        />
      )}
    </section>
  );
}

interface CorpoMovimentosProps {
  consulta: ReturnType<typeof useMovimentos>;
  page: number;
  offset: number;
  podeDesfazer: boolean;
  aoDesfazer: (movimento: MovimentoConciliado) => void;
  paginaAnterior: () => void;
  proximaPagina: () => void;
}

/**
 * Corpo da lista: trata loading/erro/vazio, renderiza os cards + a paginacao por
 * offset com o rotulo "Mostrando X-Y" (array puro, sem total).
 */
function CorpoMovimentos({
  consulta,
  page,
  offset,
  podeDesfazer,
  aoDesfazer,
  paginaAnterior,
  proximaPagina,
}: CorpoMovimentosProps): JSX.Element {
  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando conciliações…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const itens = consulta.data;

  if (itens.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Nenhuma conciliação ativa para esta empresa.
      </p>
    );
  }

  // Paginacao por offset (array puro, sem total): "Proxima" so com pagina CHEIA.
  const temProxima = itens.length === LIMITE;
  const temAnterior = page > 0;
  const inicio = offset + 1;
  const fim = offset + itens.length;

  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-4">
        {itens.map((movimento) => (
          <CardMovimentoConciliado
            key={movimento.id}
            movimento={movimento}
            podeDesfazer={podeDesfazer}
            aoDesfazer={aoDesfazer}
          />
        ))}
      </ul>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={paginaAnterior}
          disabled={!temAnterior}
          className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
        >
          Anterior
        </button>
        <button
          type="button"
          onClick={proximaPagina}
          disabled={!temProxima}
          className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
        >
          Próxima
        </button>
        <span className="font-corpo text-base text-off-black">
          Mostrando {inicio}–{fim}
        </span>
      </div>
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estavel. 503 -> indisponivel; demais
 * reusam a mensagem do `ErroHttp`. Erro nao-`ErroHttp` -> fallback generico.
 * Espelha o `mensagemErro` da Trilha/HistoricoAlertas (mesma semantica).
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Módulo de conciliação indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar as conciliações.";
}
